<template>
    <div class="flex flex-col">
        <div class="w-full flex flex-row relative">
            <div class="flex flex-row items-center relative" :class="showTextInfoElement ? 'w-1/2 flex-1' : ''">
                <label
                    :for="`control-${controlId}`"
                    class="block text-sm font-medium text-gray-700 mr-2"
                    :class="overrideLabelOpacity ? 'override-opacity' : ''">
                    {{ config.display_name }}
                </label>
                <Switch
                    v-model="controlValue"
                    :class="[
                        controlValue ? 'bg-green-400' : 'bg-neutral-300',
                        'relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out'
                    ]"
                    :true-value="true"
                    :false-value="false"
                    :readonly="!editMode || props.config.readonly || props.config.value?.readonly"
                    :disabled="!editMode"
                    :name="`control-${controlId}`"
                    aria-label="input"
                    aria-describedby="comments-description">
                    <span class="sr-only">Use setting</span>
                    <span
                        aria-hidden="true"
                        :class="[
                            controlValue ? 'translate-x-5' : 'translate-x-0',
                            'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out'
                        ]" />
                </Switch>
                <div v-if="errors.length" class="text-red-700 mt-1 text-xs">
                    <span v-for="error in errors" :key="error.$uid">* {{ error.$message }}</span>
                </div>
            </div>
            <Tooltip
                v-if="
                    hideLateralDescriptionValue &&
                    config.display_name?.trim()?.toLowerCase() !== config.description?.trim()?.toLowerCase() &&
                    config.description?.trim()?.split(' ').length > 1
                "
                :tooltip-text="config.description"
                :position="'left'"
                class="ml-2" />
            <div
                v-if="showTextInfoElement"
                class="flex-1 p-3 bg-green-50 border-l-4 border-green-200 text-xs text-black rounded-md relative arrow-left ml-4"
                :class="errors.length ? 'mb-5' : ''">
                <span v-html="config.description"></span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUpdated, computed } from 'vue';
import Tooltip from './Tooltip.vue';
import { Switch } from '@headlessui/vue';
import { sanitizeHtml } from '@/utils/utils';
import { uuid4 } from '@sentry/utils';

const controlId = uuid4();

const emit = defineEmits(['input']);
const props = defineProps({
    config: Object,
    value: Boolean,
    editMode: { type: Boolean, default: true },
    overrideLabelOpacity: Boolean,
    hideLateralDescription: { type: Boolean, default: true },
    errors: { type: Array, default: () => [] }
});

const hideLateralDescriptionValue = ref(props.hideLateralDescription);

onUpdated(() => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : !props.editMode || window.innerWidth <= 1024;
});

const defaultValue = ref(props.config.value.default === null || props.config.value.default === undefined ? false : props.config.value.default);
const controlValue = ref(props.value === null || props.value === undefined ? defaultValue.value : props.value);

const handleResize = () => {
    hideLateralDescriptionValue.value = props.hideLateralDescription ? props.hideLateralDescription : window.innerWidth <= 1024;
};

const showTextInfoElement = computed(() => {
    return (
        props.config.description &&
        !hideLateralDescriptionValue.value &&
        props.config.display_name?.trim()?.toLowerCase() !== props.config.description?.trim()?.toLowerCase() &&
        props.config.description?.trim()?.split(' ').length > 1
    );
});

window.addEventListener('resize', handleResize);
onMounted(() => {
    handleResize();
});

watch(controlValue, (newValue) => {
    emit('input', newValue);
});
</script>

<style scoped>
.arrow-left::before {
    content: '';
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 18px solid transparent;
    border-bottom: 18px solid transparent;
    border-right: 12px solid #bbf7d0;
}
</style>
